import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export type NoticeDeliveryStatus =
  | "queued"
  | "pdf_generating"
  | "ready"
  | "sending_email"
  | "sent"
  | "failed";

export interface NoticeDelivery {
  id: number;
  userId?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  status?: NoticeDeliveryStatus;
  pct?: number;
  lastError?: string | null;
  sentAt?: string | null;
  file?: {
    id?: string;
    filename?: string;
    originalname?: string;
    path?: string;
  } | null;
}

interface NoticeDeliveryState {
  deliveries: NoticeDelivery[];
  status: "idle" | "loading" | "failed";
  error: string | null;
}

const initialState: NoticeDeliveryState = {
  deliveries: [],
  status: "idle",
  error: null,
};

export const fetchNoticeDeliveries = createAsyncThunk<NoticeDelivery[], number>(
  "noticeDelivery/fetchNoticeDeliveries",
  async (noticeId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/notice-deliveries", {
        params: { noticeId },
      });
      return response.data.map((d: any) => ({
        ...d,
        firstName: d.user?.firstName,
        lastName: d.user?.lastName,
        email: d.user?.email,
      }));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const noticeDeliverySlice = createSlice({
  name: "noticeDelivery",
  initialState,
  reducers: {
    patchDelivery(
      state,
      action: PayloadAction<{
        deliveryId: number;
        updates: Partial<NoticeDelivery>;
      }>
    ) {
      const { deliveryId, updates } = action.payload;
      const idx = state.deliveries.findIndex((d) => d.id === deliveryId);
      if (idx !== -1) {
        state.deliveries[idx] = { ...state.deliveries[idx], ...updates };
      }
    },
    clearDeliveries(state) {
      state.deliveries = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNoticeDeliveries.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchNoticeDeliveries.fulfilled, (state, action) => {
        state.status = "idle";
        state.deliveries = action.payload;
      })
      .addCase(fetchNoticeDeliveries.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const { patchDelivery, clearDeliveries } = noticeDeliverySlice.actions;
export default noticeDeliverySlice.reducer;
